import React, {useEffect, useRef, useState} from 'react';
import {VideoPlayback} from '../../VideoPlayback';
import {CourseAccessGate} from '../../../modules/CourseAccessGate';
import {LessonPresentation} from '../../../data/models/Course/LessonPresentation';
import {useNavigate, useParams} from 'react-router-dom';
import {inject, observer} from 'mobx-react';
import CourseVideoPlaybackStore from '../store/CourseVideoPlaybackStore';
import {LearnerProgressModel} from '../../../data/models/LessonContent/CreateLearnerLessonModel';
import {LessonLearner} from '../../../data/models/LessonContent/LessonLearner';
import {parseLessonAnswers} from '../../VideoPlayback/lessonAnswersParset';
import {LessonType} from '../../../data/staticValues/lessonTypes';
import {
  COURSE_ORIGIN_CREATED_BY_USER,
  COURSE_ORIGIN_MICROREELS,
} from '../../../data/models/Course/Course';

interface Props {
  preview?: boolean;
  courseVideoPlaybackStore?: CourseVideoPlaybackStore;
}

const CourseVideoPlaybackContainer = inject('courseVideoPlaybackStore')(
  observer(({preview, courseVideoPlaybackStore}: Props) => {
    const [lessonPresentation, setLessonPresentation] =
      useState<LessonPresentation>();
    const [finishedLessons, setFinishedLessons] = useState<string[]>([]);

    const [progress, setProgress] = useState<LearnerProgressModel>();
    const [lessonLearner, setLessonLearner] = useState<LessonLearner>();
    const [nextLessonId, setNextLessonId] = useState<string>();
    const [isMicroreel, setIsMicroreel] = useState(false);
    const [progressError, setProgressError] = useState<string>();
    const loadVersion = useRef(0);
    const mounted = useRef(false);

    const navigate = useNavigate();
    const {courseId, lessonId, organizationId} = useParams();
    const currentLesson = useRef(`${courseId}/${lessonId}`);
    currentLesson.current = `${courseId}/${lessonId}`;
    useEffect(() => {
      mounted.current = true;
      return () => {
        mounted.current = false;
        ++loadVersion.current;
      };
    }, []);

    useEffect(() => {
      if (preview && organizationId && courseId) {
        courseVideoPlaybackStore!
          .getCourse(organizationId, courseId)
          .then(course =>
            setIsMicroreel(course?.origin === COURSE_ORIGIN_MICROREELS),
          );
      }
    }, [preview, organizationId, courseId]);

    useEffect(() => {
      if (!preview && courseId && lessonId) {
        courseVideoPlaybackStore!.createLessonLearner(courseId, {
          lessonId: lessonId,
        });
      }
    }, [preview, courseId, lessonId, finishedLessons]);

    useEffect(() => {
      initializaData();
    }, [courseVideoPlaybackStore, courseId, lessonId, preview, organizationId]);

    useEffect(() => {
      if (lessonPresentation && courseId && !preview) {
        courseVideoPlaybackStore!
          .getLearnerCourseProgress(courseId)
          .then(res => {
            if (
              res?.nextLessonId &&
              res?.nextLessonId ===
                lessonPresentation?.currentLesson.supplementalLessonId
            ) {
              navigateToSupplemental(res?.nextLessonId || '');
            }
          });
      }
    }, [lessonPresentation]);

    const initializaData = async () => {
      if (!courseId || !lessonId) return;
      const request = ++loadVersion.current;
      const identity = `${courseId}/${lessonId}`;
      const current = () =>
        mounted.current &&
        request === loadVersion.current &&
        currentLesson.current === identity;
      setLessonPresentation(undefined);
      setLessonLearner(undefined);
      setProgress(undefined);
      setProgressError(undefined);
      if (preview && organizationId) {
        await courseVideoPlaybackStore!
          .getLessonPreview(organizationId, courseId, lessonId)
          .then(result => {
            if (current()) setLessonPresentation(result);
          });
      } else {
        await courseVideoPlaybackStore!
          .getLessonPresentation(courseId, lessonId)
          .then(presentation => {
            if (!current()) return;
            // microreel groups and posts are made of microreels only
            if (
              presentation &&
              presentation.course?.origin === COURSE_ORIGIN_CREATED_BY_USER
            ) {
              presentation.lessons = presentation.lessons.filter(
                lesson => lesson.type !== LessonType.MICROREEL,
              );
            }
            setLessonPresentation(presentation);
          });
      }
      if (preview || !current()) return;
      await courseVideoPlaybackStore
        ?.getLearnerProgress(courseId, lessonId)
        .then(res => {
          if (!current()) return;
          setLessonLearner(res?.lessonLearner);
          setFinishedLessons(res?.finishedLessonIdsInCourse || []);
          setProgress(res?.progress);
        });
    };

    const onFinishedLesson = (body: LearnerProgressModel) => {
      if (courseId && lessonId) {
        const identity = `${courseId}/${lessonId}`;
        const revision = lessonPresentation?.lessonContent.studioRevisionId;
        courseVideoPlaybackStore
          ?.editLessonLearnerProgress(courseId, lessonId, body, revision)
          .then(res => {
            if (!mounted.current || currentLesson.current !== identity) return;
            setProgressError(undefined);
            setNextLessonId(res?.nextLessonId);
            setProgress(res?.progress);
            setLessonLearner(res?.lessonLearner);
          })
          .catch(error => {
            if (mounted.current && currentLesson.current === identity)
              setProgressError(error.message);
          });
      }
    };

    const getGradesResult = () => {
      if (
        progress &&
        lessonLearner &&
        lessonPresentation &&
        lessonPresentation.currentLesson.isGradable &&
        lessonPresentation.currentLesson.passingScore !== undefined
      ) {
        const grade = parseLessonAnswers(
          progress,
          lessonPresentation.lessonContent,
          lessonLearner,
          lessonPresentation.currentLesson.passingScore,
        );
        return grade;
      }
      return undefined;
    };

    const navigateToSupplemental = (supplementalLessonId: string) => {
      navigate(`/course/${courseId}/lesson/${supplementalLessonId}`, {
        replace: true,
      });
    };

    const navigateBackFromPreview = () => {
      if (isMicroreel && organizationId && courseId) {
        navigate(`/manage/${organizationId}/microreels/${courseId}`);
      } else {
        navigate(-1);
      }
    };

    const onContinue = () => {
      if (preview) {
        navigateBackFromPreview();
      } else if (nextLessonId) {
        if (nextLessonId === lessonId) {
          window.location.reload();
        } else {
          navigate(`/course/${courseId}/lesson/${nextLessonId}`, {
            replace: true,
          });
        }
      }
    };

    const onCourseHome = () => {
      if (preview) {
        navigateBackFromPreview();
      } else {
        navigate(`/course/${courseId}`, {replace: true});
      }
    };

    const onOpenLesson = (lessonId: string) => {
      navigate(`/course/${courseId}/lesson/${lessonId}`);
    };

    const navigateToLesson = async (navigateToLessonId: string) => {
      if (preview) {
        navigate(
          `/preview/${organizationId}/course/${courseId}/lesson/${navigateToLessonId}`,
        );
      } else if (lessonPresentation?.organization) {
        const publishedId =
          await courseVideoPlaybackStore!.getPublishedLessonId(
            lessonPresentation.organization.id,
            navigateToLessonId,
          );
        if (publishedId) {
          navigate(`/course/${courseId}/lesson/${publishedId}`);
        }
      }
    };

    const onBack = () => {
      navigateBackFromPreview();
    };

    const getPassingScore = () => {
      return lessonPresentation?.currentLesson?.passingScore;
    };

    return (
      <CourseAccessGate publishedCourse={!preview}>
        {progressError && <div role="alert">{progressError}</div>}
        {lessonPresentation ? (
          <VideoPlayback
            presentation={lessonPresentation}
            currentLessonId={lessonId || ''}
            gradeResult={getGradesResult()}
            preview={preview}
            onBack={onBack}
            onContinue={onContinue}
            onCourseHome={onCourseHome}
            finishedLessons={finishedLessons}
            onOpenLesson={onOpenLesson}
            passingScore={getPassingScore()}
            isLoading={courseVideoPlaybackStore?.isLoading}
            nextLessonId={nextLessonId}
            onFinishedLesson={onFinishedLesson}
            navigateToLesson={navigateToLesson}
            isMicroreel={isMicroreel}
          />
        ) : null}
      </CourseAccessGate>
    );
  }),
);

export default CourseVideoPlaybackContainer;
